/**
 * Chat runtime: sessions, WebSocket agent connection, messaging, voice and location.
 *
 * Persists conversation history per signed-in Guardian userId in localStorage.
 * Signed-out users see an empty thread with no sidebar history. Streams
 * assistant replies over WebSocket, and gates send/voice behind auth when
 * scope_profile is guardian.
 *
 * Split into cohesive modules under ./chat/ with this public API unchanged
 * (2026-09-25 gap closure; the single-file version is archived in
 * .archive/useChatApp-BAK.js). The modules share one context object from
 * useChatState and are called in a fixed order, which keeps the original
 * effect order: voice (mic release) -> sessions (sync, persist, account
 * restore) -> socket (card timers, connect, account reconnect) -> location
 * (Share-location watchdog) -> scroll.
 */
import { useEffect } from 'react';
import { hasSessionUser } from '../utils/chatSessions';
import { useChatState } from './chat/useChatState';
import { useChatVoice } from './chat/useChatVoice';
import { useChatSessions } from './chat/useChatSessions';
import { useChatSocket } from './chat/useChatSocket';
import { useChatLocation } from './chat/useChatLocation';
import { useChatTurn } from './chat/useChatTurn';
import { useChatRating } from './chat/useChatRating';

/**
 * Chat sessions, WebSocket transport, composer, voice and location.
 *
 * @param {object} options
 * @param {string|null} options.userId - Guardian user id; scopes localStorage sessions.
 * @param {string|null} options.authToken - JWT sent with each chat message.
 * @param {object|null} options.guardianUser - Full user object for message metadata.
 * @param {string} options.scopeProfile - Agent scope (`guardian`, `copilot`, etc.).
 * @param {boolean} options.requiresGuardianAuth - When true, send/voice opens auth modal.
 * @param {() => void} options.openAuthModal - Called when auth is required.
 * @param {(message?: string) => void} [options.onUnauthorized] - Called to sign the
 *   user out and reopen sign-in when a voice call comes back 401 (expired/invalid token).
 */
export function useChatApp(options) {
  const { userId } = options;
  const s = useChatState(options);
  const voice = useChatVoice(s, options);
  const sessions = useChatSessions(s, options, voice);
  const socket = useChatSocket(s, options, voice);
  const location = useChatLocation(s, options);
  const turn = useChatTurn(s, options, location);
  const rating = useChatRating(s);

  const { messages, isLoading, messagesEndRef } = s;
  // Scroll the message list when new content arrives or loading state changes.
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, messagesEndRef]);

  // Signed-out: never surface history (avoids a one-frame flash after sign-out).
  const signedIn = hasSessionUser(userId);

  return {
    // Sessions
    sessions: signedIn ? s.sessions : [],
    sessionId: signedIn ? s.sessionId : null,
    messages: signedIn ? messages : [],
    sidebarCollapsed: s.sidebarCollapsed,
    toggleSidebar: sessions.toggleSidebar,
    handleNewChat: sessions.handleNewChat,
    handleSelectSession: sessions.handleSelectSession,
    handleDeleteSession: sessions.handleDeleteSession,
    // Composer
    inputMessage: s.inputMessage,
    setInputMessage: s.setInputMessage,
    sendMessage: turn.sendMessage,
    confirmReport: turn.confirmReport,
    requestReportDraft: turn.requestReportDraft,
    handleKeyDown: turn.handleKeyDown,
    // Answer ratings (docs/guardian/03 §1.4).
    rateMessage: rating.rateMessage,
    isLoading,
    clientLocation: s.clientLocation,
    locationError: s.locationError,
    isLocating: s.isLocating,
    shareLocation: location.shareLocation,
    isRecording: s.isRecording,
    voiceError: s.voiceError,
    startVoiceRecording: voice.startVoiceRecording,
    stopVoiceRecording: voice.stopVoiceRecording,
    // WebSocket / connection (dev debug UI uses these)
    isConnected: s.isConnected,
    connectionError: s.connectionError,
    setConnectionError: s.setConnectionError,
    isRetrying: s.isRetrying,
    backendUrl: s.backendUrl,
    showUrlInput: s.showUrlInput,
    setShowUrlInput: s.setShowUrlInput,
    handleUrlChange: socket.handleUrlChange,
    retryConnection: socket.retryConnection,
    // Scroll anchor for message list
    messagesEndRef,
  };
}
